import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

import { sendSourceCommand } from '@/entities/stream/api';
import type { StreamCamera } from '@/entities/stream/types';
import { ApiError } from '@/shared/api';
import { ptzChannel, sendPtz, usePtzChannel } from './ptz-channel';

/**
 * Пульт PTZ: поворот, наклон, зум — как панель PTZ в iVMS-4200: стрелки в восемь
 * сторон, «домой» посередине, зум, скорость.
 *
 * Движение — ONVIF ContinuousMove: пока кнопку держат, камера едет; отпустили — стоп.
 * Команда идёт каналом команд источника (`POST /stream/sources/{peer}/commands`), а
 * у канала очередь и срок, поэтому у каждого «едь» есть свой `timeoutMs`: если «стоп»
 * потерялся вместе со связью, камера остановится сама, а не докрутит до упора. Пока
 * кнопку держат, «едь» повторяется чуть чаще этого срока.
 *
 * Канал общий на регистратор, поэтому в параметрах — `cameraId` канала (хвост
 * `deviceId` после `peer:`), как в снимке профилей (cameras/live-video.md §6.1).
 *
 * Первым идёт прямой путь: data channel `ptz` в сессии окна (`ptz-channel.ts`).
 * Он есть у регистратора SAMVISION и не ждёт очереди; облачные команды — запасной
 * путь для источника без канала и для «домой», которого в канале нет.
 */
export type PtzDirection =
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'upLeft'
  | 'upRight'
  | 'downLeft'
  | 'downRight';

export type PtzVector = { pan: number; tilt: number; zoom: number };

/** Ступени скорости, как ползунок 1–7 в iVMS; по умолчанию — средняя. */
export const PTZ_SPEEDS = [1, 2, 3, 4, 5, 6, 7] as const;
export const DEFAULT_PTZ_SPEED = 4;

/** Облачный путь: срок одного «едь» у камеры и шаг его повтора, пока держат. */
export const MOVE_TIMEOUT_MS = 3000;
export const MOVE_REPEAT_MS = 2400;
/**
 * Прямой канал: повтор того же «едь», пока держат. ContinuousMove без `Timeout`
 * камера исполняет СВОЙ срок по умолчанию (у Hikvision — секунды) и встаёт посреди
 * удержания — так и выглядело «держу, а она через время остановилась». Повтор раз в
 * секунду короче любого такого срока, а тот же вектор купол не переразгоняет.
 */
export const KEEPALIVE_MS = 1000;
/** Второй «стоп» после отпускания — как у пульта самого регистратора. */
export const STOP_AGAIN_MS = 350;

const AXES: Record<PtzDirection, [number, number]> = {
  up: [0, 1],
  down: [0, -1],
  left: [-1, 0],
  right: [1, 0],
  upLeft: [-1, 1],
  upRight: [1, 1],
  downLeft: [-1, -1],
  downRight: [1, -1],
};

/**
 * Скорость → доля от максимума ONVIF (−1…1): ступень/8, как на пульте самого
 * регистратора (`nvr-rust`, `ptz_speed / 8`). Полного хода нет и там: на нём купол
 * проскакивает цель быстрее, чем видео успевает это показать.
 */
function share(speed: number): number {
  const s = Math.min(Math.max(Math.round(speed), 1), PTZ_SPEEDS.length);
  return Math.round((s / 8) * 1000) / 1000;
}

export function moveVector(
  direction: PtzDirection | null,
  zoom: -1 | 0 | 1,
  speed: number,
): PtzVector {
  const v = share(speed);
  const [x, y] = direction ? AXES[direction] : [0, 0];
  return { pan: x * v, tilt: y * v, zoom: zoom * v };
}

/** Центр джойстика, где ручка ещё ничего не значит — доля радиуса. */
export const JOY_DEAD = 0.2;

/** Против часовой от «вправо», y вверх — как `atan2`. */
const SECTORS: PtzDirection[] = [
  'right',
  'upRight',
  'up',
  'upLeft',
  'left',
  'downLeft',
  'down',
  'downRight',
];

export type JoyReading = { direction: PtzDirection; speed: number };

/**
 * Положение ручки → сторона и скорость, как у джойстика Hik-Connect / EZVIZ: восемь
 * сторон (у купола всё равно восемь «чистых» ходов), скорость 1–7 по удалению от
 * центра. `x` вправо, `y` вверх, оба в долях радиуса; `null` — ручка в центре.
 */
export function readJoystick(x: number, y: number): JoyReading | null {
  const m = Math.min(1, Math.hypot(x, y));
  if (m < JOY_DEAD) {
    return null;
  }
  const sector = (Math.round(Math.atan2(y, x) / (Math.PI / 4)) + 8) % 8;
  const speed = Math.max(
    1,
    Math.ceil(((m - JOY_DEAD) / (1 - JOY_DEAD)) * PTZ_SPEEDS.length),
  );
  return { direction: SECTORS[sector], speed };
}

/** Канал внутри источника: `nvr-1:ch01` → `ch01`; одиночная камера — то же правило. */
export function ptzCameraId(camera: Pick<StreamCamera, 'deviceId' | 'peer'>) {
  const prefix = `${camera.peer}:`;
  return camera.deviceId.startsWith(prefix)
    ? camera.deviceId.slice(prefix.length)
    : camera.deviceId;
}

/** Про камеру известно, что она поворотная: флаг каталога или тип устройства. */
export function isKnownPtz(camera: StreamCamera): boolean {
  return camera.ptz === true || camera.deviceType === 'ptz_camera';
}

/**
 * Пульт у камеры: известная поворотная — всегда; `forced` (кнопка «PTZ» на полосе) —
 * у любой, кроме той, про которую источник прямо сказал `ptz: false`.
 */
export function canPtz(
  camera: StreamCamera | null | undefined,
  forced = false,
): boolean {
  if (!camera || !camera.available) {
    return false;
  }
  return isKnownPtz(camera) || (forced && camera.ptz !== false);
}

export type PtzError = 'unsupported' | 'forbidden' | 'failed';

function errorOf(e: unknown): PtzError {
  if (e instanceof ApiError) {
    // 422 — гейтвей не знает команду: PTZ ещё не включён на сервере.
    if (e.status === 422) {
      return 'unsupported';
    }
    if (e.status === 403) {
      return 'forbidden';
    }
  }
  return 'failed';
}

type Target = {
  cam: StreamCamera;
  direct: boolean;
  key: string;
  vector: PtzVector;
};

function sameVector(a: PtzVector, b: PtzVector) {
  return a.pan === b.pan && a.tilt === b.tilt && a.zoom === b.zoom;
}

/**
 * Держать — ехать, отпустить — стоп:
 *
 *   • прямой канал — `move` сразу и повтор того же вектора раз в секунду, пока
 *     держат (`KEEPALIVE_MS`): иначе камера встаёт по своему сроку;
 *   • ручку повели в другую сторону или дальше — новый `move` сразу, без «стоп»
 *     между ними, как на джойстике;
 *   • отпустили — `stop` и через 350 мс ещё один, как у пульта регистратора
 *     (`nvr-rust` `ptz_spawn_stop_now`): «едь», который был ещё в пути, может прийти
 *     после первого;
 *   • облачный путь — `move` со сроком и повтор раз в 2,4 с;
 *   • канал закрылся посреди движения — дальше через облако.
 *
 * «Стоп» идёт той камере, которая ехала, а не той, что выбрана сейчас.
 */
export function usePtz(companyId: string, camera: StreamCamera | null) {
  const [speed, setSpeed] = useState(DEFAULT_PTZ_SPEED);
  const [moving, setMoving] = useState<string | null>(null);
  const [error, setError] = useState<PtzError | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const active = useRef<Target | null>(null);
  const selected = useRef(camera);
  selected.current = camera;

  const cloud = useCallback(
    (
      cam: StreamCamera,
      command: 'ptz_move' | 'ptz_stop' | 'ptz_home',
      extra = {},
    ) => {
      sendSourceCommand(companyId, cam.peer, command, {
        cameraId: ptzCameraId(cam),
        ...extra,
      }).then(
        () => setError(null),
        e => setError(errorOf(e)),
      );
    },
    [companyId],
  );

  const push = useCallback(
    (t: Target) => {
      if (t.direct && sendPtz(t.cam.deviceId, { t: 'move', ...t.vector })) {
        setError(null);
        return;
      }
      t.direct = false;
      cloud(t.cam, 'ptz_move', { ...t.vector, timeoutMs: MOVE_TIMEOUT_MS });
    },
    [cloud],
  );

  const clear = () => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  };

  const halt = useCallback(
    (t: Target) => {
      if (t.direct && sendPtz(t.cam.deviceId, { t: 'stop' })) {
        setTimeout(() => {
          // Нажали снова — этот «стоп» уже лишний.
          if (!active.current) {
            sendPtz(t.cam.deviceId, { t: 'stop' });
          }
        }, STOP_AGAIN_MS);
        return;
      }
      cloud(t.cam, 'ptz_stop');
    },
    [cloud],
  );

  /** Начать движение или сменить его на ходу (ручку повели дальше / в сторону). */
  const start = useCallback(
    (key: string, vector: PtzVector) => {
      const cur = active.current;
      if (cur) {
        if (cur.key === key && sameVector(cur.vector, vector)) {
          return;
        }
        cur.key = key;
        cur.vector = vector;
        setMoving(key);
        push(cur);
        return;
      }
      const cam = selected.current;
      if (!cam) {
        return;
      }
      const t: Target = {
        cam,
        direct: ptzChannel(cam.deviceId) !== null,
        key,
        vector,
      };
      active.current = t;
      setMoving(key);
      push(t);
      timer.current = setInterval(
        () => active.current && push(active.current),
        t.direct ? KEEPALIVE_MS : MOVE_REPEAT_MS,
      );
    },
    [push],
  );

  const stop = useCallback(() => {
    const t = active.current;
    if (!t) {
      return;
    }
    active.current = null;
    clear();
    setMoving(null);
    halt(t);
  }, [halt]);

  const home = useCallback(() => {
    const cam = selected.current;
    if (cam) {
      cloud(cam, 'ptz_home');
    }
  }, [cloud]);

  // Другая камера или уход со страницы посреди движения — сначала стоп.
  const deviceId = camera?.deviceId;
  useEffect(() => {
    setError(null);
    return () => stop();
  }, [deviceId, stop]);

  // Отпустили не над пультом (увели мышь за окно) или ушли со вкладки — Pressable и
  // джойстик об этом могут не узнать, а купол поедет до упора.
  useEffect(() => {
    if (!moving || Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }
    const onHidden = () => {
      if (document.visibilityState === 'hidden') {
        stop();
      }
    };
    window.addEventListener('pointerup', stop);
    window.addEventListener('blur', stop);
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('blur', stop);
      document.removeEventListener('visibilitychange', onHidden);
    };
  }, [moving, stop]);

  // «Домой» по прямому каналу не ходит — у такой камеры кнопка выключена, а не
  // отвечает «сервер не принимает» при работающих стрелках.
  const direct = usePtzChannel(camera?.deviceId);

  return { speed, setSpeed, moving, error, start, stop, home, direct };
}
